import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Rocket, 
  Zap, 
  Sparkles, 
  Check,
  ChevronDown
} from "lucide-react";
import { GradientButton } from "@/components/common/ui/Button";
import { useNavigate } from 'react-router-dom';

interface Plan {
  id: number;
  name: string;
  tagline: string;
  description: string;
  icon: any;
  features: string[];
  popular?: boolean;
  isCustom?: boolean;
}

const plans: Plan[] = [
  {
    id: 1,
    name: "Starter",
    tagline: "Perfect for Small Businesses",
    description: "Get your business online with a professional website that captures your brand essence and drives customer engagement.",
    icon: Rocket,
    features: [
      "Up to 5-7 Pages",
      "Responsive Design",
      "Contact Form",
      "Basic SEO Setup",
      "Social Media Integration",
      "Mobile Optimized",
      "Basic Analytics",
      "Email Support"
    ],
    popular: false
  },
  {
    id: 2,
    name: "Professional",
    tagline: "For Growing Businesses",
    description: "Comprehensive website solution with advanced features, performance optimization, and premium support to scale your business.",
    icon: Zap,
    features: [
      "Up to 15 Pages",
      "E-commerce Integration",
      "Custom Design",
      "Content Management System",
      "Payment Gateway Setup",
      "Advanced SEO Optimization",
      "Enhanced security features for contact forms",
      "SEO-Optimized Website Structure",
      "Social Media Integration",
      "Image Optimization (ALT text & compression)",
      "Strategic Internal Linking",
      "Clean, Keyword-Rich URL Structure",
      "Site Speed Optimization (caching & minification)",
      "XML Sitemap & Robots.txt Setup",
      "Schema Markup Implementation",
      "SSL Certificate",
      "Backup & Security",
      "Advanced Analytics",
      "Priority Support"
    ],
    popular: true
  },
  {
    id: 3,
    name: "Custom Quote",
    tagline: "Tailored to Your Needs",
    description: "A fully customized solution built specifically for your business goals, with enterprise-level features and ongoing support.",
    icon: Sparkles,
    features: [
      "Client-Defined Pages",
      "Custom Development",
      "Enterprise-Level Features",
      "Dedicated Support",
      "API Integrations",
      "Advanced Security",
      "Full Technical SEO Implementation",
      "Enhanced security features for contact forms",
      "Social Media Integration",
      "Performance & Speed Optimization",
      "Schema & Structured Data Setup",
      "Scalable Architecture",
      "Ongoing Maintenance & Support",
      "Advanced Analytics & Reporting",
      "Priority Deployment",
      "SSL Certificate Included"
    ],
    isCustom: true
  }
];

export default function WebsitePlans() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleContactClick = () => {
      navigate('/request-for-services');
  };

  const toggleExpanded = (planId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPlan(expandedPlan === planId ? null : planId);
  };

  return (
    <section id="pricing-section" className="py-20 lg:py-28 relative overflow-hidden">
      {/* Animated background effects only */}
      {/* <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse" /> */}
      {/* <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} /> */}

      <div className="container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/5 rounded-full border border-white/10 backdrop-blur-sm text-xs md:text-sm font-semibold text-gray-300 tracking-wider uppercase">
            Pricing Plans
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Choose Your Website Plan
          </h2>
          <p className="text-gray-300 md:text-lg max-w-2xl mx-auto">
            From simple starter sites to enterprise solutions, we have the perfect plan for your business
          </p>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-8 max-w-7xl mx-auto items-stretch">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            const isHovered = hoveredCard === plan.id;
            const isSelected = selectedCard === plan.id;
            const isExpanded = expandedPlan === plan.id;
            const visibleFeatures = plan.features.slice(0, 5);
            const hiddenFeatures = plan.features.slice(5);
            const hasMoreFeatures = plan.features.length > 5;

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onHoverStart={() => setHoveredCard(plan.id)}
                onHoverEnd={() => setHoveredCard(null)}
                onClick={() => setSelectedCard(plan.id === selectedCard ? null : plan.id)}
                className="relative group cursor-pointer h-full"
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-0 right-0 z-20 flex justify-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wide"
                    >
                      Most Popular
                    </motion.div>
                  </div>
                )}

                {/* Card */}
                <motion.div
                  animate={{
                    scale: plan.popular && isHovered ? 1.03 : isHovered ? 1.01 : 1,
                    y: isHovered ? -8 : 0,
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className={`h-full flex flex-col bg-gradient-to-br from-purple-600/5 to-blue-600/5 backdrop-blur-xl rounded-2xl p-8 border ${
                    isSelected 
                      ? 'border-purple-500/50 ring-2 ring-purple-500/50' 
                      : plan.popular 
                      ? 'border-purple-500/30' 
                      : 'border-purple-500/20'
                  } hover:border-purple-500/50 transition-all duration-300 relative overflow-hidden`}
                >
                  {/* Gradient overlay for popular card */}
                  {plan.popular && (
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-blue-600/10 pointer-events-none" />
                  )}

                  {/* Content */}
                  <div className="relative z-10 flex flex-col flex-grow">
                    {/* Icon */}
                    <motion.div
                      animate={{ 
                        scale: isHovered ? 1.05 : 1
                      }}
                      transition={{ duration: 0.3, type: "spring" }}
                      className="w-16 h-16 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center mb-6"
                    >
                      <Icon className="w-8 h-8 text-white" strokeWidth={2} />
                    </motion.div>

                    {/* Plan Name */}
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {plan.name}
                    </h3>
                    <p className="text-sm font-medium mb-4 uppercase tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                      {plan.tagline}
                    </p>
                    <p className="text-gray-300 text-sm mb-8 leading-relaxed">
                      {plan.description}
                    </p>

                    {/* Divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent mb-8" />

                    {/* Features */}
                    <div className="space-y-3 flex-grow">
                      {/* First 5 features */}
                      {visibleFeatures.map((feature, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.05 + idx * 0.03 }}
                          className="flex items-start gap-3"
                        >
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center mt-0.5">
                            <Check className="w-3 h-3 text-white" strokeWidth={3} />
                          </div>
                          <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
                        </motion.div>
                      ))}

                      {/* Accordion for remaining features */}
                      {hasMoreFeatures && (
                        <div className="pt-2">
                          <motion.button
                            onClick={(e) => toggleExpanded(plan.id, e)}
                            className="flex items-center gap-2 text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 hover:from-purple-300 hover:to-blue-300 transition-all w-full"
                            whileHover={{ x: 2 }}
                          >
                            <motion.div
                              animate={{ rotate: isExpanded ? 180 : 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <ChevronDown className="w-4 h-4 text-purple-400" />
                            </motion.div>
                            <span>{isExpanded ? 'Show less' : `Show ${hiddenFeatures.length} more features`}</span>
                          </motion.button>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                className="overflow-hidden"
                              >
                                <div className="space-y-3 mt-3 pt-3 border-t border-purple-500/20">
                                  {hiddenFeatures.map((feature, idx) => (
                                    <motion.div
                                      key={idx}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      exit={{ opacity: 0, x: -20 }}
                                      transition={{ delay: idx * 0.05 }}
                                      className="flex items-start gap-3"
                                    >
                                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center mt-0.5">
                                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                      </div>
                                      <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Button - Now outside the cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="text-center mt-16"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block"
          >
            <GradientButton
              className="group/btn text-white font-semibold py-4 px-8 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 transition-all duration-300 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40"
              onClick={handleContactClick}
            >
              <span className="flex items-center justify-center gap-2">
                Get Started with Your Website
                {/* <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" /> */}
              </span>
            </GradientButton>
          </motion.div>
          
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center mt-6"
        >
          <p className="text-gray-300">
            Ready to transform your online presence? Let's discuss your project!
          </p>
        </motion.div>
      </div>
    </section>
  );
}